import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import MasterModal from "./MasterModal";
import MasterTable from "./MasterTable";
import { getRoles, createRole, updateRole, toggleRoleActive } from "@/services/masters";
import AddRoleForm, { AddRoleFormHandle } from "./roles/AddRole";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import MasterTableHeader from "./generics/MasterTableHeader";

interface RoleItem {
  Name: string;
  id?: string | number;
  Is_Active?: boolean;
}

interface Role {
  id?: string | number;
  name: string;
  isActive: boolean;
}

export default function Roles() {
  const { canCreate, canUpdate } = useScreenPermission("roles");
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [togglingId, setTogglingId] = useState<string | number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const formRef = useRef<AddRoleFormHandle>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const loadRoles = useCallback(async (p: number = 1, search: string = "", statusFilter: string) => {
    setIsLoading(true);
    try {
      const { data, status } = await getRoles(p, search, statusFilter);
      if (status === 200 && data) {
        const d = data as any;
        const items: RoleItem[] = d.results ?? (Array.isArray(d) ? d : []);
        setRoles(items.map((item) => ({
          id: item.id,
          name: item.Name,
          isActive: item.Is_Active ?? true,
        })));
        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count ?? 0);
      } else {
        // toast.error("Failed to load roles");
      }
    } catch (error) {
      toast.error("Error loading roles");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load + debounced search
  useEffect(() => {
    const delay = searchQuery ? 400 : 0;
    const timer = setTimeout(() => {
      setPage(1);
      loadRoles(1, searchQuery, statusFilter);
    }, delay);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, loadRoles]);

  const handleOpenModal = (role?: Role) => {
    if (role) {
      setEditingRole(role);
    } else {
      setEditingRole(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
  };

  const handleSubmit = async (data: { Name: string }) => {
    formRef.current?.clearFieldErrors();
    setIsLoading(true);
    try {
      const response = editingRole && editingRole.id
        ? await updateRole(Number(editingRole.id), data)
        : await createRole(data);

      if (response.status === 201 || response.status === 200) {
        toast.success(editingRole ? "Role updated successfully" : "Role created successfully");
        handleCloseModal();
        await loadRoles(page, searchQuery, statusFilter);
      } else if (response.errors) {
        let hasFieldErrors = false;

        if (typeof response.errors === "object") {
          Object.entries(response.errors).forEach(([key, value]) => {
            const errorText = Array.isArray(value) ? value[0] : String(value);

            if (key === "error" || key === "non_field_errors" || key === "detail") {
              toast.error(errorText);
            } else {
              formRef.current?.setFieldError(key, errorText);
              hasFieldErrors = true;
            }
          });
        }

        if (!hasFieldErrors) {
          toast.error("Failed to save role.");
        }
      }
    } catch (error) {
      console.error("Error saving role:", error);
      toast.error("Error saving role");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handleToggleActive = async (role: Role) => {
    if (!role.id) return;
    setTogglingId(role.id);
    try {
      const response = await toggleRoleActive(Number(role.id), { Is_Active: !role.isActive });
      if (response.status === 200) {
        toast.success(`Role ${!role.isActive ? "activated" : "deactivated"} successfully`);
        await loadRoles(page, searchQuery, statusFilter);
      } else {
        toast.error("Failed to update role status");
      }
    } catch (error) {
      toast.error("Error updating role status");
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    { key: "name" as const, label: "Role Name" },
  ];

  return (
    <div className="space-y-6">
      <MasterTableHeader
        title="Roles Management"
        description="Create, edit, and manage roles"
        canCreate={canCreate}
        addButtonText="Add New Role"
        onAdd={() => handleOpenModal()}
        searchPlaceholder="Search by role name..."
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showDropdownFilter
        dropdownValue={statusFilter}
        onDropdownChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        showClearFilters
        onClearFilters={() => {
          setSearchQuery("");
          setStatusFilter("");
          setPage(1);

          loadRoles(1, "", "");
        }}
      />
      <MasterTable
        columns={columns}
        data={roles}
        onEdit={handleOpenModal}
        onToggleActive={handleToggleActive}
        togglingId={togglingId}
        isLoading={isLoading && roles.length === 0}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => { setPage(p); loadRoles(p, searchQuery, statusFilter); }}
        canEdit={canUpdate}
        totalCount={totalCount}
        canToggle={canUpdate}
      />

      <MasterModal
        isOpen={isModalOpen}
        title={editingRole ? "Edit Role" : "Add New Role"}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        isLoading={isLoading}
      >
        <AddRoleForm
          ref={formRef}
          onSubmit={handleSubmit}
          defaultValues={editingRole ? { Name: editingRole.name } : undefined}
          isLoading={isLoading}
        />
      </MasterModal>
    </div>
  );
}
